// src/Pages/management/BidManagement/Bids.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Badge,
  Form,
  Spinner,
  Card,
  InputGroup,
  Modal,
  Row,
  Col,
  Pagination,
  Accordion
} from 'react-bootstrap';
import {
  Search,
  Eye,
  Trash,
  Calendar,
  Receipt,
  Person,
  Building,
  CurrencyDollar,
  Rulers,
  ArrowClockwise,
  ChevronDown,
  ChevronUp,
  GeoAlt,
  FileEarmarkPdf,
  BarChart,
  Calculator
} from 'react-bootstrap-icons';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Bids = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [selectedBid, setSelectedBid] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showProjectViewModal, setShowProjectViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [costVarianceData, setCostVarianceData] = useState([]);
  const limit = 10;

  const primaryColor = '#302302ff';
  const secondaryColor = '#0d2b27ff';

  // Fetch cost variance data for all projects
  const fetchCostVarianceData = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.get(
        'http://34.206.193.218:4001/api/admin/adminCostVariance/projectCostVariance',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setCostVarianceData(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching cost variance data:', error);
    }
  };

  // Fetch all bids and group them by project
  const fetchBids = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      const response = await axios.get('http://34.206.193.218:4001/api/admin/adminBids/allBids', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          page: currentPage,
          limit: limit,
          search: searchTerm || undefined,
          minAmount: minAmount || undefined,
          maxAmount: maxAmount || undefined
        }
      });

      if (response.data.success) {
        const bids = response.data.data || [];
        
        // Group bids by project
        const projectsMap = {};
        
        bids.forEach(bid => {
          const projectId = bid.projectId?._id || 'no-project';
          const projectName = bid.projectId?.name || 'No Project';
          const projectLocation = bid.projectId?.projectLocation || 'N/A';
          
          if (!projectsMap[projectId]) {
            projectsMap[projectId] = {
              id: projectId,
              name: projectName,
              location: projectLocation,
              createdBy: bid.createdBy,
              bids: [],
              totals: {
                totalBid: 0,
                totalActual: 0,
                totalVariance: 0
              }
            };
          }
          
          // Find cost variance data for this bid
          const projectVarianceData = costVarianceData.find(
            project => project.projectId === projectId
          );
          
          let bidVarianceData = null;
          if (projectVarianceData) {
            bidVarianceData = projectVarianceData.tasks.find(
              task => task.taskId === bid._id
            );
          }
          
          const actualCost = bidVarianceData?.actualCost || bid.actualCost || 0;
          const variance = actualCost - bid.totalAmount;
          
          projectsMap[projectId].bids.push({
            ...bid,
            actualCost,
            variance
          });
          
          projectsMap[projectId].totals.totalBid += bid.totalAmount || 0;
          projectsMap[projectId].totals.totalActual += actualCost;
          projectsMap[projectId].totals.totalVariance += variance;
        });

        const projectsArray = Object.values(projectsMap);
        setProjects(projectsArray);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('Failed to load bids');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCostVarianceData();
  }, []);

  useEffect(() => {
    if (costVarianceData.length > 0) {
      fetchBids();
    }
  }, [currentPage, searchTerm, minAmount, maxAmount, costVarianceData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleMinAmountChange = (e) => {
    setMinAmount(e.target.value);
    setCurrentPage(1);
  };

  const handleMaxAmountChange = (e) => {
    setMaxAmount(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setMinAmount('');
    setMaxAmount('');
    setCurrentPage(1);
  };

  const handleViewBid = (bid) => {
    setSelectedBid(bid);
    setShowViewModal(true);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowProjectViewModal(true);
  };

  const handleDeleteClick = (bid) => {
    setSelectedBid(bid);
    setShowDeleteModal(true);
  };

  const handleDeleteBid = async () => {
    if (!selectedBid) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      const response = await axios.delete(
        `http://34.206.193.218:4001/api/admin/adminBids/delete/${selectedBid._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success('Bid task deleted successfully');
        fetchCostVarianceData(); // Refresh data
        setShowDeleteModal(false);
        setSelectedBid(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting bid:', error);
      toast.error(error.response?.data?.message || 'Failed to delete bid task');
    } finally {
      setUpdating(false);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const getVarianceColor = (variance) => {
    if (variance < 0) return '#28a745'; // Under budget (green)
    if (variance === 0) return '#6c757d'; // On budget (gray)
    return '#dc3545'; // Over budget (red)
  };

  const getVarianceBadge = (variance) => {
    if (variance < 0) {
      return <Badge bg="success">Under Budget</Badge>;
    }
    if (variance === 0) {
      return <Badge bg="secondary">On Budget</Badge>;
    }
    return <Badge bg="danger">Over Budget</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return Number(num).toLocaleString();
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTaskName = (taskId) => {
    // This is a placeholder - you should replace this with actual task name mapping
    // Based on your data, taskName seems to be an ID, you might need another API call
    // to get actual task names, or modify your backend to include task names
    return taskId || 'Task';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let number = start; number <= end; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <div style={{ 
      backgroundColor: '#ffffffff', 
      minHeight: '100vh'
    }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER + FILTERS */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            {/* TITLE */}
            <Col md={3}>
              <h4 className="fw-bold mb-0">Bid Management</h4>
              <small className="text-muted">
                Project-wise bid tasks with variance analysis
              </small>
            </Col>

            {/* SEARCH */}
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search project or user"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Col>

            {/* MIN AMOUNT */}
            <Col md={2}>
              <InputGroup>
                <InputGroup.Text>
                  <CurrencyDollar size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Min"
                  value={minAmount}
                  onChange={handleMinAmountChange}
                  min="0"
                />
              </InputGroup>
            </Col>

            {/* MAX AMOUNT */}
            <Col md={2}>
              <InputGroup>
                <InputGroup.Text>
                  <CurrencyDollar size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Max"
                  value={maxAmount}
                  onChange={handleMaxAmountChange}
                  min="0"
                />
              </InputGroup>
            </Col>

            {/* ACTIONS */}
            <Col md={2} className="text-end">
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={clearFilters}
              >
                Clear
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => {
                  fetchCostVarianceData();
                }}
                title="Refresh"
              >
                <ArrowClockwise />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Projects Accordion */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading bid tasks...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5">
              <Receipt size={48} color="#a0aec0" className="mb-3" />
              <h5 style={{ color: '#718096' }}>No bid tasks found</h5>
              <p className="text-muted">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              {projects.map((project, projectIndex) => (
                <div key={project.id} className="border-bottom">
                  {/* Project Header */}
                  <div 
                    className="p-3 d-flex justify-content-between align-items-center cursor-pointer"
                    style={{ 
                      backgroundColor: projectIndex % 2 === 0 ? '#f8fafc' : '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleProject(project.id)}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        {expandedProjects[project.id] ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                      <div>
                        <div className="d-flex align-items-center">
                          <Building size={18} className="me-2 text-primary" />
                          <h5 className="mb-0 fw-bold" style={{ color: primaryColor }}>
                            {project.name}
                          </h5>
                          <Badge bg="info" className="ms-2">
                            {project.bids.length} {project.bids.length === 1 ? 'Task' : 'Tasks'}
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center mt-1">
                          <small className="text-muted d-flex align-items-center">
                            <GeoAlt size={12} className="me-1" />
                            {project.location}
                          </small>
                          {project.createdBy && (
                            <small className="text-muted ms-3 d-flex align-items-center">
                              <Person size={12} className="me-1" />
                              {project.createdBy.name}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="text-end">
                        <div className="fw-bold">
                          {/* <CurrencyDollar size={14} className="me-1" /> */}
                          {formatCurrency(project.totals.totalBid)}
                        </div>
                        <small className="text-muted">Total Bid</small>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">
                          {/* <CurrencyDollar size={14} className="me-1" /> */}
                          {formatCurrency(project.totals.totalActual)}
                        </div>
                        <small className="text-muted">Total Actual</small>
                      </div>
                      <div className="text-end">
                        <div 
                          className="fw-bold"
                          style={{ color: getVarianceColor(project.totals.totalVariance) }}
                        >
                          <Calculator size={14} className="me-1" />
                          {formatCurrency(project.totals.totalVariance)}
                        </div>
                        <small className="text-muted">Total Variance</small>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProject(project);
                        }}
                      >
                        <Eye size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Project Bids Table (Collapsible) */}
                  {expandedProjects[project.id] && (
                    <div className="p-3">
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead style={{ 
                            backgroundColor: secondaryColor,
                            color: 'white'
                          }}>
                            <tr>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Task</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Rate & Area</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Bid Amount</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Actual Cost</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Variance</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Status</th>
                              <th style={{ padding: '1rem', fontWeight: '600' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.bids.map((bid, bidIndex) => (
                              <tr key={bid._id}>
                                <td style={{ padding: '1rem' }}>
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                      {getTaskName(bid.taskName)}
                                    </div>
                                    <small className="text-muted">
                                      Task ID: {bid._id.slice(-8)}
                                    </small>
                                  </div>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  <div>
                                    <div style={{ color: '#4a5568', fontWeight: '500' }}>
                                      <CurrencyDollar size={12} className="me-1" />
                                      {formatNumber(bid.ratePerSqft || 0)} / sq ft
                                    </div>
                                    <small style={{ color: '#718096' }}>
                                      <Rulers size={12} className="me-1" />
                                      {formatNumber(bid.totalSqft || 0)} sq ft
                                    </small>
                                  </div>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  <div style={{ fontWeight: '600', color: primaryColor }}>
                                    {formatCurrency(bid.totalAmount)}
                                  </div>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  <div style={{ fontWeight: '600', color: '#6c757d' }}>
                                    {formatCurrency(bid.actualCost)}
                                  </div>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  <div 
                                    style={{ 
                                      fontWeight: '600',
                                      color: getVarianceColor(bid.variance)
                                    }}
                                  >
                                    {formatCurrency(bid.variance)}
                                  </div>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  {getVarianceBadge(bid.variance)}
                                </td>
                                <td style={{ padding: '1rem' }}>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => handleViewBid(bid)}
                                      style={{
                                        backgroundColor: secondaryColor,
                                        border: 'none',
                                        color: 'white'
                                      }}
                                      title="View Details"
                                    >
                                      <Eye size={16} />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteClick(bid)}
                                      style={{
                                        backgroundColor: '#dc3545',
                                        border: 'none',
                                        color: 'white'
                                      }}
                                      title="Delete Bid Task"
                                    >
                                      <Trash size={16} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} bid tasks
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* View Bid Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: primaryColor, color: 'white' }}>
          <Modal.Title className="d-flex align-items-center">
            <Receipt className="me-2" />
            Bid Task Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          {selectedBid && (
            <Row className="g-4">
              <Col md={12}>
                <div className="d-flex align-items-center mb-4">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: secondaryColor,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      marginRight: '16px'
                    }}
                  >
                    <Receipt size={24} />
                  </div>
                  <div>
                    <h4 style={{ color: '#2d3748', fontWeight: '600', margin: 0 }}>
                      {getTaskName(selectedBid.taskName)}
                    </h4>
                    <div className="mt-1">
                      <Badge bg="info" className="me-2">
                        Bid Task
                      </Badge>
                      <small className="text-muted">
                        ID: {selectedBid._id}
                      </small>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Financial Details with Variance */}
              <Col md={12}>
                <Card style={{ border: `2px solid ${primaryColor}` }}>
                  <Card.Header style={{ backgroundColor: primaryColor, color: 'white', fontWeight: '600' }}>
                    <BarChart className="me-2" />
                    Cost Analysis
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <div className="text-center p-3">
                          <div className="mb-2" style={{ color: primaryColor, fontSize: '0.9rem' }}>
                            Bid Amount
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: primaryColor }}>
                            {formatCurrency(selectedBid.totalAmount)}
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center p-3">
                          <div className="mb-2" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                            Actual Cost
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#6c757d' }}>
                            {formatCurrency(selectedBid.actualCost || 0)}
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center p-3">
                          <div className="mb-2" style={{ 
                            color: getVarianceColor(selectedBid.variance), 
                            fontSize: '0.9rem' 
                          }}>
                            Variance
                          </div>
                          <div style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '600',
                            color: getVarianceColor(selectedBid.variance)
                          }}>
                            {formatCurrency(selectedBid.variance || 0)}
                          </div>
                          <div className="mt-2">
                            {getVarianceBadge(selectedBid.variance || 0)}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Task Details */}
              <Col md={6}>
                <Card style={{ border: `2px solid ${secondaryColor}` }}>
                  <Card.Header style={{ backgroundColor: secondaryColor, color: 'white', fontWeight: '600' }}>
                    Task Information
                  </Card.Header>
                  <Card.Body>
                    <div className="row g-3">
                      <div className="col-12">
                        <label style={{ color: '#718096', fontSize: '0.9rem' }}>Task Name</label>
                        <div style={{ color: '#2d3748', fontWeight: '500' }}>
                          {getTaskName(selectedBid.taskName)}
                        </div>
                      </div>
                      <div className="col-6">
                        <label style={{ color: '#718096', fontSize: '0.9rem' }}>Rate per Sq Ft</label>
                        <div style={{ color: '#2d3748', fontWeight: '500' }}>
                          {formatCurrency(selectedBid.ratePerSqft)}
                        </div>
                      </div>
                      <div className="col-6">
                        <label style={{ color: '#718096', fontSize: '0.9rem' }}>Total Sq Ft</label>
                        <div style={{ color: '#2d3748', fontWeight: '500' }}>
                          {formatNumber(selectedBid.totalSqft)} sq ft
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Project Details */}
              {selectedBid.projectId && (
                <Col md={6}>
                  <Card style={{ border: '2px solid #e2e8f0' }}>
                    <Card.Header style={{ backgroundColor: '#e2e8f0', color: '#2d3748', fontWeight: '600' }}>
                      <Building className="me-2" />
                      Project Details
                    </Card.Header>
                    <Card.Body>
                      <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '0.5rem' }}>
                        {selectedBid.projectId.name}
                      </div>
                      <div style={{ color: '#718096' }} className="d-flex align-items-center">
                        <GeoAlt size={14} className="me-1" />
                        {selectedBid.projectId.projectLocation || 'Location not specified'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Creator Details */}
              {selectedBid.createdBy && (
                <Col md={6}>
                  <Card style={{ border: '2px solid #e2e8f0' }}>
                    <Card.Header style={{ backgroundColor: '#e2e8f0', color: '#2d3748', fontWeight: '600' }}>
                      <Person className="me-2" />
                      Created By
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex align-items-center">
                        <div
                          style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: primaryColor,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            marginRight: '12px'
                          }}
                        >
                          {getUserInitials(selectedBid.createdBy.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#2d3748' }}>
                            {selectedBid.createdBy.name}
                          </div>
                          <small style={{ color: '#718096' }}>
                            {selectedBid.createdBy.email}
                          </small>
                          <br />
                          <small style={{ color: '#718096' }}>
                            Phone: {selectedBid.createdBy.number || 'N/A'}
                          </small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Timestamps */}
              <Col md={6}>
                <Card style={{ border: '2px solid #e2e8f0' }}>
                  <Card.Header style={{ backgroundColor: '#e2e8f0', color: '#2d3748', fontWeight: '600' }}>
                    <Calendar className="me-2" />
                    Timestamps
                  </Card.Header>
                  <Card.Body>
                    <div className="row g-2">
                      <div className="col-12">
                        <label style={{ color: '#718096', fontSize: '0.9rem' }}>Created</label>
                        <div style={{ color: '#2d3748', fontWeight: '500' }} className="d-flex align-items-center">
                          <Calendar className="me-2" size={14} />
                          {formatDate(selectedBid.createdAt)}
                        </div>
                      </div>
                      {selectedBid.updatedAt && (
                        <div className="col-12">
                          <label style={{ color: '#718096', fontSize: '0.9rem' }}>Last Updated</label>
                          <div style={{ color: '#2d3748', fontWeight: '500' }} className="d-flex align-items-center">
                            <Calendar className="me-2" size={14} />
                            {formatDate(selectedBid.updatedAt)}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Project Modal */}
      <Modal show={showProjectViewModal} onHide={() => setShowProjectViewModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: primaryColor, color: 'white' }}>
          <Modal.Title className="d-flex align-items-center">
            <Building className="me-2" />
            Project Bid Summary
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          {selectedProject && (
            <>
              <div className="d-flex align-items-center mb-4">
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: primaryColor,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    marginRight: '16px'
                  }}
                >
                  <Building size={24} />
                </div>
                <div>
                  <h4 style={{ color: '#2d3748', fontWeight: '600', margin: 0 }}>
                    {selectedProject.name}
                  </h4>
                  <div className="mt-1">
                    <small className="text-muted d-flex align-items-center">
                      <GeoAlt size={14} className="me-1" />
                      {selectedProject.location}
                    </small>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm" style={{ backgroundColor: '#e3f2fd' }}>
                    <Card.Body>
                      <div className="mb-2">
                        <CurrencyDollar size={24} color={primaryColor} />
                      </div>
                      <h5 className="mb-1" style={{ color: primaryColor }}>
                        {formatCurrency(selectedProject.totals.totalBid)}
                      </h5>
                      <small className="text-muted">Total Bid Amount</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm" style={{ backgroundColor: '#e8f5e9' }}>
                    <Card.Body>
                      <div className="mb-2">
                        <CurrencyDollar size={24} color="#4caf50" />
                      </div>
                      <h5 className="mb-1" style={{ color: '#4caf50' }}>
                        {formatCurrency(selectedProject.totals.totalActual)}
                      </h5>
                      <small className="text-muted">Total Actual Cost</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm" style={{ 
                    backgroundColor: getVarianceColor(selectedProject.totals.totalVariance) === '#dc3545' 
                      ? '#ffebee' 
                      : '#e8f5e9' 
                  }}>
                    <Card.Body>
                      <div className="mb-2">
                        <Calculator size={24} color={getVarianceColor(selectedProject.totals.totalVariance)} />
                      </div>
                      <h5 
                        className="mb-1" 
                        style={{ color: getVarianceColor(selectedProject.totals.totalVariance) }}
                      >
                        {formatCurrency(selectedProject.totals.totalVariance)}
                      </h5>
                      <small className="text-muted">Total Variance</small>
                      <div className="mt-2">
                        {getVarianceBadge(selectedProject.totals.totalVariance)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Tasks Table */}
              <h6 className="mb-3" style={{ color: primaryColor, fontWeight: '600' }}>
                Bid Tasks ({selectedProject.bids.length})
              </h6>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th>Task</th>
                      <th>Bid Amount</th>
                      <th>Actual Cost</th>
                      <th>Variance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProject.bids.map((bid) => (
                      <tr key={bid._id}>
                        <td style={{ fontWeight: '500' }}>
                          {getTaskName(bid.taskName)}
                        </td>
                        <td style={{ color: primaryColor, fontWeight: '500' }}>
                          {formatCurrency(bid.totalAmount)}
                        </td>
                        <td style={{ color: '#6c757d' }}>
                          {formatCurrency(bid.actualCost)}
                        </td>
                        <td style={{ color: getVarianceColor(bid.variance), fontWeight: '500' }}>
                          {formatCurrency(bid.variance)}
                        </td>
                        <td>
                          {getVarianceBadge(bid.variance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Creator Info */}
              {selectedProject.createdBy && (
                <Card className="mt-4 border-0 shadow-sm">
                  <Card.Body>
                    <h6 style={{ color: primaryColor, fontWeight: '600' }} className="mb-3">
                      Project Owner
                    </h6>
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          backgroundColor: primaryColor,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          marginRight: '12px'
                        }}
                      >
                        {getUserInitials(selectedProject.createdBy.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#2d3748' }}>
                          {selectedProject.createdBy.name}
                        </div>
                        <small style={{ color: '#718096' }}>
                          {selectedProject.createdBy.email}
                        </small>
                        <br />
                        <small style={{ color: '#718096' }}>
                          Phone: {selectedProject.createdBy.number || 'N/A'}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProjectViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
          <Modal.Title className="d-flex align-items-center">
            <Trash className="me-2" />
            Delete Bid Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBid && (
            <div className="text-center">
              <div className="mb-4">
                <div style={{ fontSize: '4rem', color: '#dc3545' }}>
                  <Trash />
                </div>
              </div>
              
              <h5 style={{ color: '#2d3748', marginBottom: '1rem' }}>
                Delete {getTaskName(selectedBid.taskName)}?
              </h5>
              
              <div className="alert alert-danger mb-3">
                <strong>Warning:</strong> This action cannot be undone. The bid task will be permanently deleted.
              </div>
              
              <div className="text-start bg-light p-3 rounded">
                <p className="mb-1"><strong>Task Name:</strong> {getTaskName(selectedBid.taskName)}</p>
                {selectedBid.projectId && (
                  <p className="mb-1"><strong>Project:</strong> {selectedBid.projectId.name}</p>
                )}
                <p className="mb-0"><strong>Bid Amount:</strong> {formatCurrency(selectedBid.totalAmount)}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={handleDeleteBid}
            disabled={updating}
          >
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash className="me-2" />
                Delete Permanently
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Bids;